import { createContext, useContext, useEffect, useRef } from "react";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { useAccount } from "./use-account";
import { AxiosError } from "axios";
import { useGlobal } from "./use-global";

export const useDatadogProvider = (appName: string) => {
  const env = import.meta.env.VITE_REACT_APP_ENV;
  const server = import.meta.env.VITE_REACT_APP_SERVER;

  const { vojomoAccount } = useAccount();
  const { pingData } = useGlobal();
  const shouldTrack = env !== "dev";
  const isInitializedRef = useRef<boolean>(false);

  const trackError = (label: string, error: any = {}, payload: any = {}) => {
    if (!!error) {
      if (error instanceof AxiosError) {
        error = error?.response?.data || error?.response || error;

        if (error instanceof XMLHttpRequest) {
          error = JSON.stringify(error);
        }
      } else {
        error = {
          name: error.name,
          message: error.message,
          stack: error.stack
        };
      }
    }

    console.error(label, {
      payload: { error, ...payload }
    });

    if (env === "dev") {
      return;
    }

    datadogLogs.logger.error(label, {
      payload: { error, ...payload }
    });
  };

  const trackEvent = (eventName: string, eventData: any = {}) => {
    console.info(eventName, { payload: eventData });

    if (env === "dev") {
      return;
    }

    datadogLogs.logger.info(eventName, { payload: eventData });
  };

  useEffect(() => {
    if (!shouldTrack || isInitializedRef.current) return () => null;

    datadogLogs.init({
      clientToken: "pub38b74df99c877310af665fe44bbcdee4",
      site: "us5.datadoghq.com",
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: appName,
      version: pingData.version,
      env: env
    });

    datadogRum.init({
      applicationId: "ea032981-7fbd-4553-8687-3197dcbac480",
      clientToken: "pub818889056efc555e0af3160c9cdae489",
      site: "us5.datadoghq.com",
      service: appName,
      sessionSampleRate: 100,
      trackUserInteractions: true,
      version: pingData.version,
      //   defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT,
      env: env,
      allowedTracingUrls: [(url) => url.startsWith(server)],
      sessionReplaySampleRate: 100,
      trackBfcacheViews: true,
      defaultPrivacyLevel: "allow"
    });

    datadogRum.startSessionReplayRecording();

    isInitializedRef.current = true;
  }, [shouldTrack]);

  useEffect(() => {
    if (!!vojomoAccount) {
      //   datadogLogs.setGlobalContextProperty("extraPayload", {
      //     isPWA,
      //   });

      datadogLogs.setUser({
        id: vojomoAccount["_id"],
        email: vojomoAccount.email,
        name: vojomoAccount.name
      });

      //   datadogRum.setGlobalContextProperty("extraPayload", {
      //     isPWA,
      //   });

      datadogRum.setUser({
        id: vojomoAccount["_id"],
        email: vojomoAccount.email,
        name: vojomoAccount.name
      });
    }
  }, [vojomoAccount]);

  return {
    trackError,
    trackEvent
  };
};

export const DatadogProvider = ({ appName, children }) => {
  const accountProvider = useDatadogProvider(appName);

  return (
    <DatadogContext.Provider value={accountProvider}>
      {children}
    </DatadogContext.Provider>
  );
};

export type DatadogProps = ReturnType<typeof useDatadogProvider>;
export const DatadogContext = createContext<DatadogProps>(null);
export const useDatadog = () => useContext(DatadogContext);
